import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import 'react-phone-input-2/lib/style.css'
import { Icon } from "@iconify/react";
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import FooterTailwind from 'TailwindcssComponents/Footer';
import { motion } from "framer-motion";

export default function AddEventOrganization() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const bearerToken = localStorage.getItem('token');
  const navigate = useNavigate();
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [organizationImagePreview, setOrganizationImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [socialLinks, setSocialLinks] = useState([]);
  const prevSlugRef = useRef('');
  const debounceTimer = useRef(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activePreviewImage, setActivePreviewImage] = useState(null);

  const socialPlatforms = [
    { name: 'Instagram', prefix: 'https://instagram.com/' },
    { name: 'Facebook', prefix: 'https://facebook.com/' },
    { name: 'YouTube', prefix: 'https://youtube.com/' },
    { name: 'TikTok', prefix: 'https://tiktok.com/@' },
    { name: 'LinkedIn', prefix: 'https://linkedin.com/in/' },
    { name: 'Twitter', prefix: 'https://twitter.com/' }
  ];
  const maxPlatforms = socialPlatforms.length;
  const selectedPlatforms = socialLinks.map(link => link.platform).filter(Boolean);
  const allSelected = selectedPlatforms.length >= maxPlatforms;



  const handleAddSocialLink = () => {
    setSocialLinks([...socialLinks, { platform: '', url: '' }]);
  };

  const handleRemoveSocialLink = (index) => {
    setSocialLinks(socialLinks.filter((_, i) => i !== index));
  };

  const handleSocialLinkChange = (index, field, value) => {
    const updatedLinks = socialLinks.map((link, i) =>
      i === index ? { ...link, [field]: value } : link
    );
    setSocialLinks(updatedLinks);
    newOrganization.setFieldValue("socialLinks", updatedLinks); // Updates Formik state
  };

  const handleCoverImageChange = (event) => {
    const file = event.currentTarget.files[0];
    newOrganization.setFieldValue('coverImage', file);
    newOrganization.setFieldTouched('coverImage', true);
    if (file) {
      setCoverImagePreview(URL.createObjectURL(file));
    }
  };

  const handleOrganizationImageChange = (event) => {
    const file = event.currentTarget.files[0];
    newOrganization.setFieldValue('orgImage', file);
    newOrganization.setFieldTouched('orgImage', true);
    if (file) {
      setOrganizationImagePreview(URL.createObjectURL(file));
    }
  };

  const onSubmit = async (values, { resetForm }) => {
    setIsLoading(true);
    try {
      await validateSlug(values.slug, newOrganization.setFieldError, newOrganization.setFieldTouched);
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('email', values.email);
      formData.append('phone', values.phone);
      formData.append('address', values.address);
      formData.append('website', values.website);
      formData.append('slug', values.slug);
      const socialMediaJSON = socialLinks.reduce((acc, link) => {
        if (link.platform && link.url) {
          acc[link.platform] = link.url;
        }
        return acc;
      }, {});
      formData.append('socialMedia', JSON.stringify(socialMediaJSON));

      if (values.orgImage) {
        formData.append('orgImage', values.orgImage);
      }
      if (values.coverImage) {
        formData.append('coverImage', values.coverImage);
      }

      const response = await axios.post(`${apiUrl}auth-service/org`, formData,
        {
          headers: { 'Authorization': `Bearer ${bearerToken}` }
        });
      if (response.data?.success) {
        toast.success(response.data?.message || 'Organization has been successfully created!');
        resetForm();
        setTimeout(() => {
          navigate('/eventOrganizations');
        }, 1500);
      } else {
        throw new Error('Failed to create organization');
      }
    } catch (error) {
      console.error(error.response.data?.message || error.message);
      toast.error(error.response.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const newOrganization = useFormik({
    initialValues: {
      name: '',
      slug: '',
      email: '',
      phone: '',
      address: '',
      website: '',
      orgImage: '',
      coverImage: '',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required('Required'),
      slug: Yup.string().required("Required"),
      phone: Yup.string()
        .required("Required")
        .matches(/^\+94\d{9}$/, "Phone number must start with +94 and contain 9 digits after it")
        .length(12, "Phone number must be exactly 12 characters"),
      email: Yup.string()
        .required('Required')
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email')
        .email('Enter a valid email'),
      website: Yup.string()
        .nullable()
        .matches(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/, {
          message: 'Enter a valid URL',
        }),
      socialLinks: Yup.array().of(
        Yup.object().shape({
          platform: Yup.string().required('Please select a platform'),
          url: Yup.string()
            .required('Social media link is required')
            .matches(
              /^(https?:\/\/)?([\w.-]+)\.([a-z.]{2,6})(:[0-9]{1,5})?(\/[\w@\/.-]*)*\/?$/,
              'Enter a valid social media URL'
            ),
        })
      ),
      coverImage: Yup.mixed()
        .nullable()
        .notRequired()
        .test("validCoverImage", "Invalid cover image", (value) => {
          if (!value) return true; // Not required
          if (typeof value === "object") return true; // File object (new upload)
          if (typeof value === "string" && value.startsWith("http")) return true; // Existing image URL
          return false;
        })
        .test("fileType", "Only image files are allowed", (value) => {
          if (!value || typeof value === "string") return true; // Allow existing image URLs or empty
          return ["image/jpeg", "image/png", "image/gif"].includes(value.type);
        })
        .test("fileSize", "File size must be less than 1MB", (value) => {
          if (!value || typeof value === "string") return true; // Allow existing URLs or no file
          return value.size <= 1 * 1024 * 1024;
        }),
      orgImage: Yup.mixed()
        .nullable()
        .notRequired()
        .test("validOrgImage", "Invalid org image", (value) => {
          if (!value) return true; // Not required
          if (typeof value === "object") return true; // File object (new upload)
          if (typeof value === "string" && value.startsWith("http")) return true; // Existing image URL
          return false;
        })
        .test("fileType", "Only image files are allowed", (value) => {
          if (!value || typeof value === "string") return true; // Allow existing image URLs or empty
          return ["image/jpeg", "image/png", "image/gif"].includes(value.type);
        })
        .test("fileSize", "File size must be less than 1MB", (value) => {
          if (!value || typeof value === "string") return true; // Allow existing URLs or no file
          return value.size <= 1 * 1024 * 1024;
        }),
    }),
    onSubmit,
  });

  const validateSlug = async (slug, setFieldError, setFieldTouched) => {
    if (!slug) return;
    setFieldTouched("slug", true);
    try {
      const response = await axios.get(`${apiUrl}auth-service/org/check-slug/${slug}`);
      if (!response.data.success) {
        setFieldError("slug", response.data.message || "Slug is not available");
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setFieldError("slug", error.response.data.message || "Slug already taken");
      } else {
        setFieldError("slug", "Error validating slug. Please try again.");
      }
    }
  };

  useEffect(() => {
    const { name } = newOrganization.values;
    if (name.trim()) {
      const slug = name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .trim();

      newOrganization.setFieldValue('slug', slug);

      if (slug !== prevSlugRef.current) {
        prevSlugRef.current = slug;

        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
          validateSlug(slug, newOrganization.setFieldError, newOrganization.setFieldTouched);
        }, 300);
      }
    }
  }, [newOrganization.values.name]);

  return (
    <TailwindLayout>
      <TopBar />
      <div className="max-w-7xl mx-auto p-12 sm:p-12 lg:p-12 mb-8">
        <form onSubmit={newOrganization.handleSubmit} >
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">

            <div className="p-6">
              <div className="space-y-6">
                {/* Organization Info Section */}
                <div>
                  <h2 className="text-xl font-semibold mb-4">Create New Organization</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6">
                    {/* Left Column - Form Fields */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={newOrganization.values.name}
                          onChange={newOrganization.handleChange}
                          onBlur={newOrganization.handleBlur}
                          placeholder="Organization Name"
                          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                        />
                        {newOrganization.touched.name && newOrganization.errors.name && <div className="text-red-500">{newOrganization.errors.name}</div>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Unique Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="slug"
                          value={newOrganization.values.slug}
                          onChange={newOrganization.handleChange}
                          onBlur={() => validateSlug(newOrganization.values.slug, newOrganization.setFieldError, newOrganization.setFieldTouched)}
                          placeholder="eg. the-grand-organization"
                          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                        />
                        {newOrganization.touched.slug && newOrganization.errors.slug && <div className="text-red-500">{newOrganization.errors.slug}</div>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Contact Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          placeholder="e.g. abdc@zmail.com"
                          value={newOrganization.values.email}
                          onChange={newOrganization.handleChange}
                          onBlur={newOrganization.handleBlur}
                          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                        />
                        {newOrganization.touched.email && newOrganization.errors.email && <div className="text-red-500">{newOrganization.errors.email}</div>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Contact Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="e.g. +1234567890"
                          value={newOrganization.values.phone}
                          onChange={newOrganization.handleChange}
                          onBlur={newOrganization.handleBlur}
                          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                        />
                        {newOrganization.touched.phone && newOrganization.errors.phone && <div className="text-red-500">{newOrganization.errors.phone}</div>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Address
                        </label>
                        <input
                          type="text"
                          name="address"
                          placeholder="Address"
                          value={newOrganization.values.address}
                          onChange={newOrganization.handleChange}
                          onBlur={newOrganization.handleBlur}
                          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                        />
                        {newOrganization.touched.address && newOrganization.errors.address && <div className="text-red-500">{newOrganization.errors.address}</div>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Website Link
                        </label>
                        <input
                          type="url"
                          name="website"
                          placeholder="e.g. https://example.com"
                          value={newOrganization.values.website}
                          onChange={newOrganization.handleChange}
                          onBlur={newOrganization.handleBlur}
                          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                        />
                        {newOrganization.touched.website && newOrganization.errors.website && <div className="text-red-500">{newOrganization.errors.website}</div>}
                      </div>
                    </div>

                    {/* Right Column - Image Uploads */}
                    <div className="space-y-6">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Logo Image <span className="text-gray-500">(Recommended size: below 1mb, 1:1, JPG/JPEG/PNG format)</span></h4>
                        <div className="flex items-center space-x-4">
                          <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center relative">
                            {organizationImagePreview ? (
                              <>
                                <img src={organizationImagePreview} alt="Organization Logo" className="w-full h-full object-cover rounded-md cursor-pointer" onClick={() => {
                                  setActivePreviewImage(organizationImagePreview);
                                  setIsPreviewOpen(true);
                                }} />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOrganizationImagePreview(null);
                                    newOrganization.setFieldValue('orgImage', null);
                                    newOrganization.setFieldTouched('orgImage', true);
                                  }}
                                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg transition-colors"
                                  aria-label="Remove image"
                                >
                                  <Icon icon="material-symbols-light:close-rounded" className="text-lg" />
                                </button>
                              </>
                            ) : (
                              <Icon icon="iconoir:add-media-image" className="text-xl" />
                            )}
                          </div>
                          <label className="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 cursor-pointer">
                            <span>{organizationImagePreview ? 'Change' : 'Upload'}</span>
                            <input type="file" className="hidden" onChange={handleOrganizationImageChange} />
                          </label>
                        </div>
                        {newOrganization.touched.orgImage && newOrganization.errors.orgImage && (
                          <p className="text-red-500 text-sm mt-1">{newOrganization.errors.orgImage}</p>
                        )}
                      </div>

                      <div>
                        <h4 className="text-sm font-medium mb-2">Cover Image <span className="text-gray-500">(Recommended size: below 1mb, 4:1, JPG/JPEG/PNG format)</span></h4>
                        <div className="flex items-center space-x-4">
                          <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center relative">
                            {coverImagePreview ? (
                              <>
                                <img src={coverImagePreview} alt="Cover" className="w-full h-full object-cover rounded-md cursor-pointer" onClick={() => {
                                  setActivePreviewImage(coverImagePreview);
                                  setIsPreviewOpen(true);
                                }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCoverImagePreview(null);
                                    newOrganization.setFieldValue('coverImage', null);
                                    newOrganization.setFieldTouched('coverImage', true);
                                  }}
                                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg transition-colors"
                                  aria-label="Remove image"
                                >
                                  <Icon icon="material-symbols-light:close-rounded" className="text-lg" />
                                </button>
                              </>
                            ) : (
                              <Icon icon="iconoir:add-media-image" className="text-xl" />
                            )}
                          </div>
                          <label className="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 cursor-pointer">
                            <span>{coverImagePreview ? 'Change' : 'Upload'}</span>
                            <input type="file" className="hidden" onChange={handleCoverImageChange} />
                          </label>
                        </div>
                        {newOrganization.touched.coverImage && newOrganization.errors.coverImage && (
                          <p className="text-red-500 text-sm mt-1">{newOrganization.errors.coverImage}</p>
                        )}
                      </div>
                      {isPreviewOpen && activePreviewImage && (
                        <motion.div
                          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <motion.div
                            className="relative max-w-3xl max-h-[90vh]"
                            initial={{ scale: 0.85, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.25, ease: "easeOut" }}
                          >
                            {/* Large Image */}
                            <img
                              src={activePreviewImage}
                              alt="Preview"
                              className="rounded-lg max-h-[90vh] object-contain"
                            />

                            {/* Close Button */}
                            <button
                              onClick={() => setIsPreviewOpen(false)}
                              className="absolute -top-3 -right-3 bg-white text-black rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-gray-200 transition"
                            >
                              <Icon icon="material-symbols-light:close-rounded" className="text-xl" />
                            </button>
                          </motion.div>
                        </motion.div>
                      )}


                    </div>
                  </div>
                </div>

                {/* Social Media Links Section */}
                <div>
                  <h3 className="font-medium text-sm">Social Media Links</h3>
                  <div className="space-y-4">
                    {socialLinks.map((link, index) => (
                      <div key={index} className="group relative flex items-baseline gap-4 p-2 rounded-lg">
                        {/* Platform Dropdown */}
                        <div className="flex-1">
                          <label className="block text-sm font-medium mb-1">
                            Platform
                          </label>
                          <div className="relative">
                            <select
                              value={link.platform}
                              onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)}
                              className="w-full h-10 px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                            >
                              <option value="">Select Platform</option>
                              {socialPlatforms
                                .filter((platform) =>
                                  !selectedPlatforms.includes(platform.name) || platform.name === link.platform
                                )
                                .map((platform) => (
                                  <option key={platform.name} value={platform.name}>
                                    {platform.name}
                                  </option>
                                ))}

                            </select>
                            {newOrganization.errors.socialLinks &&
                              newOrganization.errors.socialLinks[index] &&
                              newOrganization.errors.socialLinks[index].platform && (
                                <div className="text-red-500 text-sm">
                                  {newOrganization.errors.socialLinks[index].platform}
                                </div>
                              )}
                          </div>
                        </div>
                        {/* URL Input */}
                        <div className="flex-1">
                          <label className="block text-sm font-medium mb-1">
                            Link
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="e.g. https://instagram.com/username"
                              value={link.url}
                              onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                              className="w-full h-10 px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                            />
                            {newOrganization.errors.socialLinks &&
                              newOrganization.errors.socialLinks[index] &&
                              newOrganization.errors.socialLinks[index].url && (
                                <div className="text-red-500 text-sm">
                                  {newOrganization.errors.socialLinks[index].url}
                                </div>
                              )}
                          </div>
                        </div>

                        {/* Remove Button*/}
                        <div className='self-end'>
                          <button
                            type="button"
                            onClick={() => handleRemoveSocialLink(index)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
                          >
                            <Icon icon="mdi-light:delete" className="text-xl" />
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Add Button */}
                    {!allSelected && (
                      <button
                        type="button"
                        onClick={handleAddSocialLink}
                        className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Social Media Link
                      </button>
                    )}

                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex items-center">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
        {/* loading modal spinner while saving */}
        {isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-lg flex flex-col items-center shadow-lg">
              <svg
                className="animate-spin h-8 w-8 text-blue-600 mb-3"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>

              <p className="text-sm font-medium text-gray-700">Saving...</p>
            </div>
          </div>
        )}
      </div>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <FooterTailwind />
    </TailwindLayout>
  )
}